/**
 * Sentry.io implementation
 */
import * as Sentry from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import { browserHistory } from 'react-router';

// Import environment variables from centralized location
import { 
  sentryDsn, 
  sentryEnableInDev, 
  sentryTracesSampleRate, 
  appVersion 
} from './env';

// Get environment from Node process
const NODE_ENV = process.env.NODE_ENV || 'development';

/**
 * Initialize Sentry
 * This should be called as early as possible in the application lifecycle
 */
export const initSentry = () => {
  // Initialize Sentry with configuration

  // Only initialize when DSN is available (from env variables)
  if (!sentryDsn) {
    // Silent fail in production, only log in development
    if (NODE_ENV !== 'production') {
      console.warn('Sentry initialization skipped.');
    }
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: NODE_ENV,
    // Use the same release format as in webpack.prod.babel.js
    release: appVersion,
    // Disable debug in production
    debug: NODE_ENV !== 'production',
    integrations: [
      // Basic integrations should be enough for React Router v3
      new BrowserTracing({
        tracingOrigins: ['localhost', /^\//],
      }),
    ],
    // Set trace sample rate according to configuration
    tracesSampleRate: sentryTracesSampleRate,
    // Enable Sentry based on environment
    enabled: NODE_ENV === 'production' || sentryEnableInDev,
    // Allow personally identifiable information to be sent
    // This is needed for better error context and debugging
    sendDefaultPii: true,
    // Allow additional data to be sent with events
    maxBreadcrumbs: 50,
    autoSessionTracking: true,
    // Before sending an event to Sentry, you can inspect/modify it
    beforeSend(event) {
      console.log('event', event);
      
      // Ensure user data is preserved
      if (event.user?.email) {
        // Make sure email is not scrubbed
        event._meta = event._meta || {};
        event._meta.user = event._meta.user || {};
        
        // Ensure all user fields are explicitly set
        if (event.user.email) {
          event._meta.user.email = { '': 'explicitlySet' };
        }
        
        if (event.user.companyAccountId) {
          event._meta.user.companyAccountId = { '': 'explicitlySet' };
          
          // Ensure the companyAccountId is in tags for better visibility
          event.tags = event.tags || {};
          event.tags['user.companyAccountId'] = event.user.companyAccountId;
        }
        
        // Add user data to contexts as well for better visibility
        event.contexts = event.contexts || {};
        event.contexts.user = { ...event.user };
      }
      
      // Log event in dev to help with debugging
      if (NODE_ENV !== 'production') {
        console.debug('Sentry: Sending event:', {
          eventId: event.event_id,
          hasUser: !!event.user,
          hasEmail: event.user && !!event.user.email,
          hasCompanyId: event.user && !!event.user.companyAccountId,
          tags: event.tags
        });
      }
      
      // Check for specific errors to ignore
      if (event.exception?.values?.length > 0) {
        // Check if the error message contains the ResizeObserver error string
        for (const exception of event.exception.values) {
          if (exception.value && exception.value.includes('ResizeObserver loop completed with undelivered notifications')) {
            // Return null to prevent this error from being sent to Sentry
            return null;
          }
          
          // We're capturing all TypeError errors, including map-related errors
        }
        
        // Limit the size of exception values
        event.exception.values = event.exception.values.map(value => {
          // Truncate very large error messages
          if (value.value && value.value.length > 500) {
            value.value = value.value.substring(0, 500) + '... [truncated]';
          }
          
          // Limit stacktrace frames to max 5 frames to reduce size (reduced from 10)
          if (value.stacktrace?.frames) {
            if (value.stacktrace.frames.length > 5) {
              value.stacktrace.frames = value.stacktrace.frames.slice(0, 5);
            }
            
            // Clean up each frame to reduce data size further
            value.stacktrace.frames = value.stacktrace.frames.map(frame => {
              // Keep only essential frame information
              const essentialFrame = {
                filename: frame.filename,
                function: frame.function,
                lineno: frame.lineno,
                colno: frame.colno
              };
              return essentialFrame;
            });
          }
          return value;
        });
      }
      
      // Strictly limit contexts data size
      if (event.contexts) {
        Object.keys(event.contexts).forEach(key => {
          const context = event.contexts[key];
          if (typeof context === 'object' && context !== null) {
            // Remove any potentially large properties
            delete context.memory;
            delete context.storage;
            delete context.screenshots;
            delete context.payload;
            delete context.response;
          }
        });
      }
      
      // Ensure user data is explicitly set as tags for better visibility in Sentry UI
      if (event.user) {
        // Set user email and companyAccountId as explicit tags
        event.tags = event.tags || {};
        
        if (event.user.email) {
          event.tags['user.email'] = event.user.email;
        }
        
        if (event.user.companyAccountId) {
          event.tags['user.companyAccountId'] = event.user.companyAccountId;
        }
      }

      // Limit payload size to prevent envelope size exceeded errors
      if (event.extra) {
        // Convert large objects to simple strings to reduce payload size
        Object.keys(event.extra).forEach(key => {
          const value = event.extra[key];
          
          // Special case for user-related data - preserve these
          if (key.includes('user') || key.includes('account')) {
            // Keep user data intact but limit size if needed
            if (typeof value === 'string' && value.length > 300) {
              event.extra[key] = value.substring(0, 300) + '... [truncated]';
            }
          } else if (typeof value === 'object' && value !== null) {
            // For objects that might be large, just keep type info instead of full data
            event.extra[key] = `[Object type: ${value.constructor ? value.constructor.name : typeof value}]`;
          } else if (typeof value === 'string' && value.length > 200) {
            // Truncate long strings
            event.extra[key] = value.substring(0, 200) + '... [truncated]';
          }
        });
      }

      // Return the modified event
      return event;
    },
  });

  // Add router change listener for manual breadcrumbs
  if (typeof browserHistory?.listen === 'function') {
    browserHistory.listen((location) => {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Navigation to ${location.pathname}`,
        data: {
          from: window.location.pathname,
          to: location.pathname,
          search: location.search,
        },
        level: 'info',
      });
    });
  }

  // Set user information if available from JWT or other auth mechanisms
  // Call this function after user logs in
  Sentry.configureScope((scope) => {
    scope.setTag('app.version', appVersion);
    scope.setTag('react_router', '3.x');
  });
};

/**
 * Update Sentry context with user data from Redux store
 * Call this whenever user data is available/updated in Redux
 * @param {Object} userData Redux userData object
 */
export const updateSentryContext = (userData) => {
  // Exit early if userData is not provided or is null/undefined
  if (!userData) {
    console.debug('Sentry: userData not provided to updateSentryContext');
    return;
  }
  
  try {
    // userData from Redux might be an Immutable.js object
    // We need to check if it has get method (Immutable) or use it directly (plain object)
    let email, id, companyAccountId;
    
    if (typeof userData.get === 'function') {
      // Handle Immutable.js objects safely
      try {
        email = userData.get('emailAddress');
        id = userData.get('userAccountId') || userData.get('id');
        companyAccountId = userData.get('companyAccountId');
        
        // Debug log what we extracted from Immutable object
        console.debug('Sentry: Extracted user info from Immutable:', { 
          email, 
          id, 
          companyAccountId,
          hasEmail: !!email,
          hasId: !!id,
          hasCompanyId: !!companyAccountId
        });
      } catch (e) {
        console.debug('Error accessing Immutable userData properties:', e);
        return; // Exit if we can't access properties
      }
    } else {
      // Handle plain objects
      email = userData.emailAddress;
      id = userData.userAccountId || userData.id;
      companyAccountId = userData.companyAccountId;
      
      // Debug log what we extracted from plain object
      console.debug('Sentry: Extracted user info from plain object:', { 
        email, 
        id, 
        companyAccountId,
        hasEmail: !!email,
        hasId: !!id,
        hasCompanyId: !!companyAccountId
      });
    }

    // Exit if essential user info is missing
    if (!id || !email) {
      console.debug('Sentry: Missing essential user info (id or email), skipping context update');
      return;
    }
    
    // Create a user object that will be used for both scope.setUser and custom context
    const userInfo = {
      id: id,
      email: email,
      username: email,
      companyAccountId: companyAccountId || null, // Ensure companyAccountId is not undefined
    };
    
    Sentry.configureScope((scope) => {
      // Set user info in Sentry scope
      scope.setUser(userInfo);
      
      // Add additional context as tags for better filtering in Sentry UI
      scope.setTag('user.email', email);
      if (companyAccountId) {
        scope.setTag('user.companyAccountId', companyAccountId);
      }
      
      // Add user info as additional context data
      scope.setContext('user_details', userInfo);
      
      // Force the companyAccountId to appear in multiple places for better visibility
      if (companyAccountId) {
        // Set as explicit context
        scope.setContext('company', {
          id: companyAccountId,
          user_email: email
        });
        
        // Also add as extras
        scope.setExtra('companyAccountId', companyAccountId);
        scope.setExtra('userEmail', email);
      }
      
      console.debug('Sentry: Successfully updated user context', userInfo);
    });
  } catch (error) {
    // Catch any unexpected errors to prevent app from crashing
    console.error('Error updating Sentry context:', error);
  }
};

/**
 * Manually capture an exception and send it to Sentry
 * @param {Error} error The error object
 * @param {Object} context Additional context information
 */
export const captureException = (error, context = {}) => {
  Sentry.withScope((scope) => {
    Object.keys(context).forEach((key) => {
      scope.setExtra(key, context[key]);
    });
    Sentry.captureException(error);
  });
};

/**
 * Manually capture a message and send it to Sentry
 * @param {String} message The message to capture
 * @param {Object} context Additional context information
 */
export const captureMessage = (message, context = {}) => {
  Sentry.withScope((scope) => {
    Object.keys(context).forEach((key) => {
      scope.setExtra(key, context[key]);
    });
    Sentry.captureMessage(message);
  });
};

export default {
  initSentry,
  updateSentryContext,
  captureException,
  captureMessage,
};
